import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import bundledContent from "./site.content.json";
import type { SiteContentData } from "./site";

const defaultData = bundledContent as SiteContentData;

interface SiteContentContextValue {
  data: SiteContentData;
  refresh: () => Promise<void>;
}

const SiteContentContext = createContext<SiteContentContextValue>({
  data: defaultData,
  refresh: async () => {},
});

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<SiteContentData>(defaultData);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/content", { cache: "no-store" });
      if (!response.ok) return;
      const fresh = (await response.json()) as SiteContentData;
      setData(fresh);
    } catch {
      // Bundled content already rendered — a network hiccup here just
      // means the visitor sees content as of the last deploy.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <SiteContentContext.Provider value={{ data, refresh }}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent(): SiteContentData {
  return useContext(SiteContentContext).data;
}

export function useSiteContentRefresh(): () => Promise<void> {
  return useContext(SiteContentContext).refresh;
}
